import { useState, useEffect, useCallback } from "react";
import { praxisApi, praxisPoolApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Save, ChevronDown, ChevronRight, Eye, EyeOff } from "lucide-react";

const csv = (arr) => (arr || []).join(", ");
const parse = (str) => (str || "").split(",").map((s) => s.trim()).filter(Boolean);

const GOOGLE_TYPES_LABEL = "Google-Orts-Typen (technische Google-Werte, bleiben englisch – z. B. doctor, psychologist)";

export const SpecialtyManagerDialog = ({ open, onOpenChange, onChanged }) => {
  const toast = useToast();
  const [specialties, setSpecialties] = useState([]);
  const [counts, setCounts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [edit, setEdit] = useState({});
  const [creating, setCreating] = useState(false);
  const [newSpec, setNewSpec] = useState({ specialty_key: "", display_name_de: "", search_terms: "", google_maps_terms: "", google_place_types: "", exclude_terms: "" });

  const load = useCallback(async () => {
    try {
      const res = await praxisApi.getSpecialties();
      setSpecialties(res.data);
    } catch { toast.error("Fachrichtungen konnten nicht geladen werden"); }
    try {
      const cRes = await praxisPoolApi.counts();
      setCounts(cRes.data || {});
    } catch { /* Zählanzeige ist optional */ }
  }, [toast]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (s) => {
    if (expanded === s.id) { setExpanded(null); return; }
    setExpanded(s.id);
    setEdit({
      display_name_de: s.display_name_de,
      search_terms: csv(s.search_terms),
      google_maps_terms: csv(s.google_maps_terms),
      google_place_types: csv(s.google_place_types),
      exclude_terms: csv(s.exclude_terms),
    });
  };

  const saveEdit = async (s) => {
    try {
      await praxisApi.updateSpecialty(s.id, {
        display_name_de: edit.display_name_de,
        search_terms: parse(edit.search_terms),
        google_maps_terms: parse(edit.google_maps_terms),
        google_place_types: parse(edit.google_place_types),
        exclude_terms: parse(edit.exclude_terms),
      });
      toast.success("Fachrichtung gespeichert");
      await load(); onChanged?.();
    } catch { toast.error("Speichern fehlgeschlagen"); }
  };

  const remove = async (s) => {
    try {
      await praxisApi.deleteSpecialty(s.id);
      toast.success("Fachrichtung gelöscht");
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const toggleActive = async (s) => {
    const next = s.is_active === false;
    // optimistisch aktualisieren
    setSpecialties((prev) => prev.map((x) => (x.id === s.id ? { ...x, is_active: next } : x)));
    try {
      await praxisApi.updateSpecialty(s.id, { is_active: next });
      toast.success(next ? `„${s.display_name_de}" ist aktiv` : `„${s.display_name_de}" ausgeblendet`);
      onChanged?.();
    } catch {
      setSpecialties((prev) => prev.map((x) => (x.id === s.id ? { ...x, is_active: !next } : x)));
      toast.error("Status konnte nicht geändert werden");
    }
  };

  const create = async () => {
    if (!newSpec.specialty_key.trim() || !newSpec.display_name_de.trim())
      return toast.error("Schlüssel und Anzeigename sind Pflicht");
    try {
      await praxisApi.createSpecialty({
        specialty_key: newSpec.specialty_key.trim().toLowerCase().replace(/\s+/g, "_"),
        display_name_de: newSpec.display_name_de.trim(),
        search_terms: parse(newSpec.search_terms),
        google_maps_terms: parse(newSpec.google_maps_terms),
        google_place_types: parse(newSpec.google_place_types),
        exclude_terms: parse(newSpec.exclude_terms),
      });
      toast.success("Fachrichtung angelegt");
      setCreating(false);
      setNewSpec({ specialty_key: "", display_name_de: "", search_terms: "", google_maps_terms: "", google_place_types: "", exclude_terms: "" });
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="specialty-manager-dialog">
        <DialogHeader>
          <DialogTitle>Fachrichtungen konfigurieren</DialogTitle>
          <DialogDescription>Suchbegriffe, Google-Begriffe und Ausschlussbegriffe je Fachrichtung pflegen.</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] pr-3">
          <div className="space-y-2">
            {specialties.map((s) => {
              const hidden = s.is_active === false;
              return (
              <div key={s.id} className={`border rounded-lg transition-opacity ${hidden ? "opacity-55 bg-muted/30" : ""}`} data-testid={`specialty-${s.specialty_key}`}>
                <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => toggle(s)}>
                  <div className="flex items-center gap-2 min-w-0">
                    {expanded === s.id ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    <span className="font-medium truncate">{s.display_name_de}</span>
                    {typeof counts[s.specialty_key] === "number" && (
                      <Badge variant="outline" className="text-xs shrink-0 text-muted-foreground" data-testid={`specialty-count-${s.specialty_key}`}>
                        {counts[s.specialty_key]} im Pool
                      </Badge>
                    )}
                    {s.is_default && <Badge variant="secondary" className="text-xs shrink-0">Standard</Badge>}
                    {hidden && <Badge variant="outline" className="text-xs shrink-0 text-muted-foreground">ausgeblendet</Badge>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5" title={hidden ? "Ausgeblendet – einschalten, um in der Übersicht zu zeigen" : "Aktiv"}>
                      {hidden ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-emerald-600" />}
                      <Switch checked={!hidden} onCheckedChange={() => toggleActive(s)} data-testid={`specialty-toggle-${s.specialty_key}`} />
                    </div>
                    {!s.is_default && (
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(s)} data-testid={`specialty-delete-${s.specialty_key}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {expanded === s.id && (
                  <div className="p-3 pt-0 space-y-3 border-t">
                    <div>
                      <Label className="text-xs">Anzeigename</Label>
                      <Input value={edit.display_name_de} onChange={(e) => setEdit({ ...edit, display_name_de: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Suchbegriffe (Komma-getrennt)</Label>
                      <Input value={edit.search_terms} onChange={(e) => setEdit({ ...edit, search_terms: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Google-Maps-Begriffe</Label>
                      <Input value={edit.google_maps_terms} onChange={(e) => setEdit({ ...edit, google_maps_terms: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">{GOOGLE_TYPES_LABEL}</Label>
                      <Input value={edit.google_place_types} onChange={(e) => setEdit({ ...edit, google_place_types: e.target.value })} placeholder="doctor" data-testid="edit-place-types" />
                    </div>
                    <div>
                      <Label className="text-xs">Ausschlussbegriffe</Label>
                      <Input value={edit.exclude_terms} onChange={(e) => setEdit({ ...edit, exclude_terms: e.target.value })} />
                    </div>
                    <Button size="sm" onClick={() => saveEdit(s)} data-testid={`specialty-save-${s.specialty_key}`}>
                      <Save className="h-4 w-4 mr-2" /> Speichern
                    </Button>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {creating ? (
            <div className="border rounded-lg p-3 mt-3 space-y-3 bg-muted/40" data-testid="new-specialty-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Schlüssel (key)</Label>
                  <Input value={newSpec.specialty_key} onChange={(e) => setNewSpec({ ...newSpec, specialty_key: e.target.value })} placeholder="z. B. cardiology" />
                </div>
                <div>
                  <Label className="text-xs">Anzeigename</Label>
                  <Input value={newSpec.display_name_de} onChange={(e) => setNewSpec({ ...newSpec, display_name_de: e.target.value })} placeholder="z. B. Kardiologie" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Suchbegriffe</Label>
                <Input value={newSpec.search_terms} onChange={(e) => setNewSpec({ ...newSpec, search_terms: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Google-Maps-Begriffe</Label>
                <Input value={newSpec.google_maps_terms} onChange={(e) => setNewSpec({ ...newSpec, google_maps_terms: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{GOOGLE_TYPES_LABEL}</Label>
                <Input value={newSpec.google_place_types} onChange={(e) => setNewSpec({ ...newSpec, google_place_types: e.target.value })} placeholder="doctor" data-testid="new-place-types" />
              </div>
              <div>
                <Label className="text-xs">Ausschlussbegriffe</Label>
                <Input value={newSpec.exclude_terms} onChange={(e) => setNewSpec({ ...newSpec, exclude_terms: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={create} data-testid="create-specialty-submit">Anlegen</Button>
                <Button size="sm" variant="outline" onClick={() => setCreating(false)}>Abbrechen</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" className="mt-3 w-full" onClick={() => setCreating(true)} data-testid="add-specialty-btn">
              <Plus className="h-4 w-4 mr-2" /> Fachrichtung hinzufügen
            </Button>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
